import { useState } from "react";
import { fetchCheck as apiCheck, fetchUsage, fetchSettings, formatAmount, formatPhone, currentMonthKey } from "@/lib/staff-api";

export default function UsagePage() {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{
    name: string; store: string; department?: string | null; position?: string | null;
    used: number; remaining: number; limit: number;
    discountRate: number;
    resetDay: number;
    rows: { id: number; store: string; amount: number; purchaseAmount: number | null; createdAt: string; status?: string }[];
  } | null>(null);

  const displayPhone = phone.length === 11
    ? `${phone.slice(0,3)}-${phone.slice(3,7)}-${phone.slice(7)}`
    : phone.length === 10 ? `${phone.slice(0,3)}-${phone.slice(3,6)}-${phone.slice(6)}` : phone;

  async function handleCheck() {
    if (phone.length < 9) { setError("전화번호를 정확히 입력하세요"); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const [check, settings] = await Promise.all([
        apiCheck(phone),
        fetchSettings().catch(() => ({ monthly_limit: 300000, discount_rate: 30, discount_pin: "", reset_day: 1 })),
      ]);
      if (!check.employee) {
        setError(check.reason === "NOT_FOUND" ? "등록되지 않은 전화번호입니다" : "비활성화된 직원입니다");
        setLoading(false); return;
      }
      const mk = currentMonthKey();
      const usageRows = await fetchUsage({ employeeId: check.employee.id, monthKey: mk, includeAll: true });
      setResult({
        name: check.employee.name,
        store: check.employee.store,
        department: check.employee.department,
        position: check.employee.position,
        used: check.used ?? 0,
        remaining: check.remaining ?? 0,
        limit: check.limit ?? 300000,
        discountRate: check.discountRate ?? settings.discount_rate,
        resetDay: settings.reset_day || 1,
        rows: usageRows.map(r => ({ id: r.id, store: r.store, amount: r.amount, purchaseAmount: r.purchaseAmount ?? null, createdAt: r.createdAt, status: r.status })),
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "조회 오류");
    } finally {
      setLoading(false);
    }
  }

  const pct = result ? Math.min(100, Math.round((result.used / result.limit) * 100)) : 0;
  const mk = currentMonthKey();

  // 취소되지 않은 활성 행만 필터
  const activeRows = result ? result.rows.filter(r => r.status !== "cancelled") : [];
  // 이번달 실제 할인받은 금액 합계 (취소 제외)
  const totalDiscount = activeRows.reduce((s, r) => s + r.amount, 0);
  // 잔여 구매가능액 기준 추가로 받을 수 있는 할인 예상액
  const additionalDiscount = result ? Math.floor(result.remaining * result.discountRate / 100) : 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-primary text-primary-foreground">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold tracking-tight">MIMILINE</span>
            <span className="text-xs opacity-70 font-medium">내 할인 잔액 조회</span>
          </div>
          <a href="#/" className="text-sm font-medium px-3 py-1.5 rounded-md bg-primary-foreground/15 hover:bg-primary-foreground/25 transition-colors">← 돌아가기</a>
        </div>
      </header>

      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-5 flex flex-col gap-4">
        <div className="bg-card rounded-lg border border-border p-4 flex flex-col gap-3">
          <p className="text-sm font-semibold">전화번호로 내 할인 현황 조회</p>
          <p className="text-xs text-muted-foreground">본인의 전화번호를 입력하면 이번달 할인 사용 현황을 확인할 수 있습니다.</p>
          <div className="flex gap-2">
            <input
              type="tel" value={displayPhone}
              onChange={e => { const d = e.target.value.replace(/\D/g, "").slice(0,11); setPhone(d); setError(""); setResult(null); }}
              onKeyDown={e => e.key === "Enter" && handleCheck()}
              placeholder="010-0000-0000"
              className="flex-1 border border-border rounded-md px-3 py-2.5 text-base font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-ring bg-background"
            />
            <button onClick={handleCheck} disabled={loading || phone.length < 9}
              className="px-4 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-md disabled:opacity-50 hover:opacity-90 transition-opacity">
              {loading ? "조회중…" : "조회"}
            </button>
          </div>
          {error && <p className="text-sm text-destructive font-medium">{error}</p>}
        </div>

        {result && (
          <>
            {/* 직원 정보 */}
            <div className="bg-card rounded-lg border border-border p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg flex-shrink-0">
                    {result.name[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold">{result.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[result.department, result.position, result.store].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-muted-foreground leading-tight">한도 리셋일</p>
                  <p className="text-xs font-semibold text-primary leading-tight">매월 {result.resetDay}일</p>
                </div>
              </div>

              {/* 구매한도 현황 */}
              <div className="border-t border-border pt-3 flex flex-col gap-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{mk} 구매 한도 현황</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-muted/40 rounded-lg p-2.5">
                    <p className="text-xs text-muted-foreground mb-0.5">월 구매 한도</p>
                    <p className="text-sm font-bold text-foreground">{formatAmount(result.limit)}</p>
                  </div>
                  <div className="bg-accent/30 rounded-lg p-2.5">
                    <p className="text-xs text-muted-foreground mb-0.5">이번달 구매</p>
                    <p className="text-sm font-bold text-primary">{formatAmount(result.used)}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2.5">
                    <p className="text-xs text-muted-foreground mb-0.5">잔여 구매 가능</p>
                    <p className="text-sm font-bold text-green-700">{formatAmount(result.remaining)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>구매 한도 사용률</span><span>{pct}%</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-yellow-500" : "bg-primary"}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>

              {/* 할인금액 현황 */}
              <div className="border-t border-border pt-3 flex flex-col gap-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{mk} 할인 현황 (직원할인 {result.discountRate}%)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-rose-50 border border-rose-100 rounded-lg p-3 flex flex-col items-center gap-0.5">
                    <p className="text-xs text-rose-500 font-medium">이번달 할인받은 금액</p>
                    <p className="text-lg font-bold text-rose-600">− {formatAmount(totalDiscount)}</p>
                    <p className="text-xs text-muted-foreground">{activeRows.length}건</p>
                  </div>
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3 flex flex-col items-center gap-0.5">
                    <p className="text-xs text-green-600 font-medium">추가 할인 가능 예상액</p>
                    <p className="text-lg font-bold text-green-700">− {formatAmount(additionalDiscount)}</p>
                    <p className="text-xs text-muted-foreground">잔여 {formatAmount(result.remaining)} 기준</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 이번달 사용 내역 */}
            <div className="bg-card rounded-lg border border-border p-4 flex flex-col gap-3">
              <p className="text-sm font-semibold">이번달 사용 내역 ({activeRows.length}건)</p>
              {result.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">이번달 사용 내역이 없습니다</p>
              ) : (
                <div className="flex flex-col divide-y divide-border">
                  {result.rows.map(r => {
                    const isCancelled = r.status === "cancelled";
                    return (
                    <div key={r.id} className={`flex items-center justify-between py-2.5 ${isCancelled ? "opacity-50" : ""}`}>
                      <div>
                        <p className={`text-xs text-muted-foreground ${isCancelled ? "line-through" : ""}`}>{r.createdAt.slice(0,16).replace("T"," ")}</p>
                        <div className="flex items-center gap-1.5">
                          <p className={`text-sm text-muted-foreground ${isCancelled ? "line-through" : ""}`}>{r.store}</p>
                          {isCancelled && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-500">취소됨</span>
                          )}
                        </div>
                        {r.purchaseAmount != null && (
                          <p className={`text-xs text-muted-foreground ${isCancelled ? "line-through" : ""}`}>구매 {formatAmount(r.purchaseAmount)}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${isCancelled ? "text-gray-400 line-through" : "text-rose-600"}`}>− {formatAmount(r.amount)}</p>
                        {r.purchaseAmount != null && !isCancelled && (
                          <p className="text-xs text-muted-foreground">실결제 {formatAmount(r.purchaseAmount - r.amount)}</p>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              <div className="border-t border-border pt-2 flex flex-col gap-1">
                <div className="flex justify-between text-sm font-semibold">
                  <span>할인금액 합계</span>
                  <span className="text-rose-600">− {formatAmount(totalDiscount)}</span>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>구매금액 합계</span>
                  <span>{formatAmount(result.used)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
